import { useState, useEffect } from "react";
import "./formStyle.scss";
import { useNavigate } from "react-router-dom";
import {z, ZodType} from "zod";
import axios from "axios";
import { notification } from "antd";

// icon
import {
  userIcon,
  passwordIcon,
  visibleIcon,
  invisibleIcon,
  checkboxEmptyIcon,
  checkboxCheckedIcon,
  buildingIcon,
  emailIcon
} from "../../constants/icons";

// utils
import monitorInputChange from "../../utils/monitorInputChange";

// interface
import { ErrorParams } from "../../interfaces/errorParams";

interface SignUpdata{
  name: string;
  email: string;
  password: string;
  organisation: string;
}

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";

const Signup = () => {
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const [formdata, setFormdata] = useState<SignUpdata>({
    name: "",
    email: "",
    password: "",
    organisation: "",
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [agree, setAgree] = useState<boolean>(false);
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };


  const signupSchema:ZodType<SignUpdata> = z.object({
    name: z.string().min(2).max(80),
    email: z.string().email(),
    password: z.string().min(6),
    organisation: z.string().min(1).max(150)
  })

  const handleValidation = () =>{
    let errorlist:any[] = [];
    const validation = signupSchema.safeParse(formdata);
    if(!validation.success){
      const {errors} = validation.error;
      errors.forEach(err =>{
        errorlist.push({
          path: err.path[0],
          message: err.message
        })
      })
    }
    if(agree === false){
      errorlist.push({
        path: "terms",
        message: "Please accept terms of service"
      })
    }
    setErrorParams(errorlist)

    if(errorlist.length >0){
      return false;
    }else{
      return true;
    }
  }

  const handleUserSignup = async() =>{
    try {
      setLoading(true)
      const validate = handleValidation();
      if(!validate){
        setLoading(false);
        return false;
      }
      const res = await axios.post(`${API_URL}/auth/signup`,formdata);   
      if(res.status === 201 && res.data.success === true){
        return navigate("/auth/signin?action=create&status=success")
      }

    } catch (error:any) {
      if(error.response.status === 400 && 
        error.response.data.success === false &&
        error.response.data.error === "email-exist"
      ){
        opNotification("error", "Account Exists", "Account already exists!");
        return false;
      }
    }finally{
      setLoading(false)
    }
  }


  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        if(err.path !== "terms"){
          let ele = document.getElementById(err.path);
        if (ele) {
          ele.style.borderColor = "#FF7875";
        }
        }else if(err.path === "terms"){
          // eslint-disable-next-line
          opNotification("warning", "Terms of service", "You must agree to terms of service.")
        }
        
      });
    }
    // eslint-disable-next-line
  }, [errorParams]);

  return (
    <div className="formContainer">
      {contextHolder}
      <span className="title">Sign up to HAIQU</span>
      <form
        className="formHolder"
        onSubmit={(e) => {
          e.preventDefault();
          handleUserSignup();
        }}
      >
        <div className="inputHolder" id="name">
          <div className="iconHolder">{userIcon}</div>
          <input
            type="text"
            value={formdata.name}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                name: e.target.value,
              });
              monitorInputChange("name")
            }}
            placeholder="Name & Surname"
          />
        </div>
        <div className="inputHolder" id="email">
          <div className="iconHolder">{emailIcon}</div>
          <input
            type="email"
            value={formdata.email}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                email: e.target.value,
              });
              monitorInputChange("email")
            }}
            placeholder="E-mail"
          />
        </div>
        <div className="inputHolder" id="password">
          <div className="iconHolder">{passwordIcon}</div>
          <input
            type={showPassword ? "text" : "password"}
            value={formdata.password}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                password: e.target.value,
              });
              monitorInputChange("password")
            }}
            placeholder="Password (atleast 6 characters)"
          />
          <div
            className="iconHolder toggle"
            onClick={() => {
              setShowPassword((prev) => !prev);
            }}
          >
            {showPassword ? invisibleIcon : visibleIcon}
          </div>
        </div>
        <div className="inputHolder" id="organisation">
          <div className="iconHolder">{buildingIcon}</div>
          <input
            type="text"
            value={formdata.organisation}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                organisation: e.target.value,
              });
              monitorInputChange("organisation")
            }}
            placeholder="Organisation"
          />
        </div>

        <div className="actionHolder center">
          <span className="checkboxHolder" style={{ alignSelf: "center" }}>
            <div
              className={`iconHolder ${agree ? "active" : ""}`}
              onClick={() => {
                setAgree((prev) => !prev);
              }}
            >
              {agree ? checkboxCheckedIcon : checkboxEmptyIcon}
            </div>
            <span>I agree to the terms of service</span>
          </span>
        </div>

        <button type="submit" className="submit" disabled={loading ? true: false}>
          {
            loading ? "Signing up..." : "Sign up"
          } 
        </button>

        <div className="formfooterCenter">
          <span>Already have an account?</span>
          <span
            className="link"
            onClick={() => {
              navigate("/auth/signin");
            }}
          >
            Sign in!
          </span>
        </div>
      </form>
    </div>
  );
};

export default Signup;
